/**
 * Sends the signed-in user's Firebase ID token on our own API calls (request-auth phase 0).
 * Thin wiring only — WHICH requests get it is decided by src/utils/authHeaderPolicy.js, and WHEN a request must wait for
 * the session by src/utils/authSessionGate.js (both pure, both tested). This file owns the environment: the Firebase token
 * listener and axios registration.
 *
 * NEVER BLOCKS ON FIREBASE CALLS: the token is kept in memory by onIdTokenChanged (Firebase refreshes it in the background)
 * and the interceptor reads that copy synchronously. It never awaits getIdToken / waitForAuthUser — that wait can block for
 * seconds on a refresh. No user or no token → the request goes out exactly as it does today.
 *
 * ONE BOUNDED EXCEPTION (phase 4, prototype 2026-10-19): on a hard page load the first calls fire before Firebase has restored
 * the session (measured: ~0.2 s earlier than the first tokened call), so they would go out tokenless and a backend that
 * enforces the login check would refuse them. A request the policy WOULD tag, fired before Firebase has said who is signed in,
 * is held until it has — at most SESSION_WAIT_CAP_MS from app start, then it goes out as it always did. Requests after the
 * session is known (a user, or nobody) and requests the policy never tags are untouched.
 *
 * SECOND BOUNDED EXCEPTION (2026-11-03): a SIGNED-IN user whose cached token is unusable (expired, inside the 60 s skew, blank — a
 * woken laptop, a throttled hidden tab) used to go out tokenless, and a route that enforces the login check answered 401. Such a
 * request now waits at most REFRESH_WAIT_CAP_MS for a forced refresh (src/utils/authTokenRefresh.js, injected here), then carries
 * the fresh token — or goes out as always if the refresh failed or hung. Healthy tokens, signed-out tabs and untagged requests
 * never wait. This file still never calls getIdToken itself.
 *
 * Install BEFORE installPhiMaskInterceptor() in main.js: axios 0.18 runs request interceptors in
 * reverse registration order, so the demo-mask send block still runs first. Pinned by
 * cypress/node-tests/auth-token-wiring.test.js.
 */

import axios from 'axios';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { normalizeApiBase } from '@/utils/authHeaderPolicy';
import { createSessionGate, createAuthRequestHandler } from '@/utils/authSessionGate';
import { deadSessionHold, isDeadSessionCandidate } from '@/utils/deadSessionHold';
import { createTokenRefresher } from '@/utils/authTokenRefresh';
import { DEMO_SESSION_HINT_KEY } from '@/utils/demo/demoSession';

const API_BASE = normalizeApiBase(process.env.VUE_APP_TEAMIO_API_URL);

let auth = null;
let gate = null;
let current = { token: null, expiresAt: 0 };
let generation = 0; // a late token result from a previous user/session never overwrites a newer state
let authedRouteProbe = null; // () => boolean, registered by main.js once the router exists

/** main.js hands us "does the current route need a login" (the guard's own rule: any matched record has meta.requiresAuth). */
export function setAuthedRouteProbe(fn) {
  authedRouteProbe = typeof fn === 'function' ? fn : null;
}

function readLocal(key) {
  try { return window.localStorage.getItem(key); } catch (_e) { return null; }
}

// Dead-session hold (src/utils/deadSessionHold.js): on Firebase's FIRST answer, "nobody" on a tab that believes it is logged in
// holds the tagged requests until the session-health guard has healed or signed the tab out. Flag off (the default) ⇒ never begins.
function beginDeadSessionHold() {
  try {
    if (!isDeadSessionCandidate({
      flagOn: process.env.VUE_APP_DEAD_SESSION_GUARD === 'true',
      firstAnswer: !gate.isSettled(),
      authenticated: readLocal('authenticated') === 'true',
      authedRoute: !!(authedRouteProbe && authedRouteProbe()),
      demoHint: readLocal(DEMO_SESSION_HINT_KEY) === 'true',
      guardOff: readLocal('teamio_session_guard') === 'off', // sessionHealthGuard.KILL_SWITCH_KEY (a test pins the two together)
    })) return;
    deadSessionHold.begin();
  } catch (_e) { /* the hold is an extra; any surprise leaves requests exactly as today */ }
}

function trackToken() {
  auth = getAuth();
  onIdTokenChanged(auth, (user) => {
    const mine = ++generation;
    current = { token: null, expiresAt: 0 };
    if (!user) { beginDeadSessionHold(); gate.settle(); return; } // Firebase answered: nobody is signed in
    // Called from inside onIdTokenChanged, this returns the token Firebase just issued (no network).
    user.getIdTokenResult().then((result) => {
      if (mine !== generation || !result) return;
      const exp = result.claims && Number(result.claims.exp);
      current = { token: result.token, expiresAt: exp ? exp * 1000 : 0 };
    }).catch(() => {}).then(() => gate.settle()); // every outcome settles, so a failure never holds a request to the cap
  });
}

export function installAuthTokenInterceptor() {
  let refresher = null;
  try {
    gate = createSessionGate(); // the cap counts from here (app start)
    trackToken();
    // Stale-token refresh (2026-11-03): a SIGNED-IN tab whose cached token is expired / inside the skew / blank (a woken laptop, a
    // throttled hidden tab) waits, bounded, for a fresh token instead of sending tokenless. The Firebase call lives in the pure
    // module; this file only hands it the current user and keeps its own cached copy in step.
    refresher = createTokenRefresher({
      getUser: () => (auth && auth.currentUser) || null,
      onFresh: (fresh) => { current = { token: fresh.token, expiresAt: fresh.expiresAt }; },
    });
  } catch (_e) {
    return; // no Firebase → nothing to attach; every request stays exactly as today
  }
  axios.interceptors.request.use(createAuthRequestHandler({
    gate,
    hold: deadSessionHold,
    refresh: () => refresher.refresh(),
    apiBase: API_BASE,
    getCtx: () => ({
      apiBase: API_BASE,
      token: current.token,
      expiresAt: current.expiresAt,
      now: Date.now(),
      signedIn: !!(auth && auth.currentUser),
    }),
  }));
}
